import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
  Paper,
  Divider,
  Stack,
  useTheme,
  useMediaQuery,
  Chip,
  Avatar
} from '@mui/material';
import {
  Close as CloseIcon,
  GetApp as DownloadIcon,
  Smartphone as PhoneIcon,
  Apple as AppleIcon,
  Android as AndroidIcon,
  Share as ShareIcon,
  Home as HomeIcon,
  AddBox as AddIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components for better visual hierarchy
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '20px',
    background: theme.palette.mode === 'dark' 
      ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
      : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    backdropFilter: 'blur(20px)',
    border: theme.palette.mode === 'dark' 
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : '1px solid rgba(0, 0, 0, 0.1)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: '12px 24px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  background: 'linear-gradient(135deg, #7ead49 0%, #a4d470 100%)',
  boxShadow: '0 4px 15px rgba(126, 173, 73, 0.4)',
  '&:hover': {
    background: 'linear-gradient(135deg, #6d9a40 0%, #7ead49 100%)',
    boxShadow: '0 6px 20px rgba(126, 173, 73, 0.6)',
  },
}));

const FeatureCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 16px',
  borderRadius: '12px',
  background: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.05)'
    : 'rgba(126, 173, 73, 0.1)',
  border: `1px solid ${theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)'
    : 'rgba(126, 173, 73, 0.2)'}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  }
}));

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // Detect if app is already installed/in standalone mode
    const checkStandalone = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                        window.navigator.standalone ||
                        document.referrer.includes('android-app://');
      setIsStandalone(standalone);
    };

    // Detect platform
    const detectPlatform = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
      const isAndroidDevice = /android/.test(userAgent);
      
      setIsIOS(isIOSDevice);
      setIsAndroid(isAndroidDevice);
    };

    checkStandalone();
    detectPlatform();

    // Listen for the beforeinstallprompt event (Android)
    const handleBeforeInstallPrompt = (e) => {
      console.log('beforeinstallprompt event fired');
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Only show prompt if not already installed and not shown in this session
      if (!isStandalone && !sessionStorage.getItem('pwa-prompt-dismissed')) {
        setTimeout(() => {
          setShowInstallPrompt(true);
        }, 3000); // Show after 3 seconds
      }
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      console.log('PWA was installed');
      setSnackbar({
        open: true,
        message: 'ALTAIR FINANCE app installed successfully! 🎉',
        severity: 'success'
      });
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
      localStorage.setItem('pwa-installed', 'true');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Check for iOS install prompt
    if (isIOS && !isStandalone && !sessionStorage.getItem('ios-prompt-dismissed')) {
      setTimeout(() => {
        setShowIOSPrompt(true);
      }, 5000); // Show after 5 seconds for iOS
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isStandalone, isIOS]);

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) {
      setSnackbar({
        open: true,
        message: 'Installation not available. Please try again later.',
        severity: 'warning'
      });
      return;
    }

    try {
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        setSnackbar({
          open: true,
          message: 'Installing ALTAIR FINANCE app...',
          severity: 'info'
        });
      } else {
        console.log('User dismissed the install prompt');
        setSnackbar({
          open: true,
          message: 'Installation cancelled',
          severity: 'info'
        });
      }
      
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
      sessionStorage.setItem('pwa-prompt-dismissed', 'true');
    } catch (error) {
      console.error('Error during installation:', error);
      setSnackbar({
        open: true,
        message: 'Installation failed. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleDismiss = (promptType = 'android') => {
    if (promptType === 'ios') {
      setShowIOSPrompt(false);
      sessionStorage.setItem('ios-prompt-dismissed', 'true');
    } else {
      setShowInstallPrompt(false);
      sessionStorage.setItem('pwa-prompt-dismissed', 'true');
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Don't show anything if already installed
  if (isStandalone) return null;

  return (
    <>
      {/* Android Install Prompt */}
      <StyledDialog
        open={showInstallPrompt}
        onClose={() => handleDismiss('android')}
        maxWidth="sm"
        fullWidth
        scroll="body"
        sx={{
          zIndex: 9999,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(4px)',
          }
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            pb: 1,
            position: 'relative'
          }}
        >
          <IconButton
            onClick={() => handleDismiss('android')}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary'
            }}
          >
            <CloseIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Avatar
              src="/logos/altair-appicon-main.png"
              sx={{ width: 80, height: 80, mb: 2 }}
            />
          </Box>
          
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Install ALTAIR FINANCE
          </Typography>
          
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Get the full mining dashboard experience with our mobile app
          </Typography>
          
          <Chip
            icon={<DownloadIcon />}
            label="Progressive Web App"
            color="primary"
            variant="outlined"
            sx={{ 
              background: 'rgba(126, 173, 73, 0.1)',
              borderColor: '#7ead49',
              color: '#7ead49'
            }}
          />
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 2 }}>
          <Stack spacing={2} sx={{ mb: 3 }}>
            <FeatureCard>
              <SecurityIcon sx={{ color: '#7ead49', fontSize: 24 }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Enhanced Security
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Bank-level encryption and secure mining
                </Typography>
              </Box>
            </FeatureCard>
            
            <FeatureCard>
              <SpeedIcon sx={{ color: '#7ead49', fontSize: 24 }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Lightning Fast
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Real-time mining data and instant updates
                </Typography>
              </Box>
            </FeatureCard>
            
            <FeatureCard>
              <TrendingIcon sx={{ color: '#7ead49', fontSize: 24 }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  AI-Powered Mining
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Advanced mining analytics and tools
                </Typography>
              </Box>
            </FeatureCard>
          </Stack>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            p: 2, 
            borderRadius: 2,
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(126, 173, 73, 0.1)',
            border: `1px solid ${theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(126, 173, 73, 0.2)'}`
          }}>
            <AndroidIcon sx={{ color: '#7ead49', fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontSize: '14px' }}>
              Works offline • No app store required • Automatic updates
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            onClick={() => handleDismiss('android')}
            variant="outlined"
            fullWidth
            sx={{ 
              borderRadius: '12px',
              borderColor: 'text.secondary',
              color: 'text.secondary'
            }}
          >
            Maybe Later
          </Button>
          
          <StyledButton
            onClick={handleAndroidInstall}
            fullWidth
            startIcon={<AddIcon />}
          >
            Install App
          </StyledButton>
        </DialogActions>
      </StyledDialog>

      {/* iOS Install Prompt */}
      <StyledDialog
        open={showIOSPrompt}
        onClose={() => handleDismiss('ios')}
        maxWidth="sm"
        fullWidth
        scroll="body"
        sx={{
          zIndex: 9999,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(4px)',
          }
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            pb: 1,
            position: 'relative'
          }}
        >
          <IconButton
            onClick={() => handleDismiss('ios')}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary'
            }}
          >
            <CloseIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Avatar
              src="/logos/altair-appicon-main.png"
              sx={{ width: 80, height: 80, mb: 2 }}
            />
          </Box>
          
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Add to Home Screen
          </Typography>
          
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Install ALTAIR FINANCE for the best mining experience on iOS
          </Typography>
          
          <Chip
            icon={<AppleIcon />}
            label="iOS Web App"
            color="primary"
            variant="outlined"
            sx={{ 
              background: 'rgba(126, 173, 73, 0.1)',
              borderColor: '#7ead49',
              color: '#7ead49'
            }}
          />
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 2 }}>
          <Box sx={{ 
            p: 3, 
            borderRadius: 2,
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(126, 173, 73, 0.1)',
            border: `1px solid ${theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(126, 173, 73, 0.2)'}`
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              How to install:
            </Typography>
            
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  width: 24, 
                  height: 24, 
                  borderRadius: '50%', 
                  background: '#7ead49',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  1
                </Box>
                <Typography variant="body2">
                  Tap the <ShareIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} /> Share button
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  width: 24, 
                  height: 24, 
                  borderRadius: '50%', 
                  background: '#7ead49',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  2
                </Box>
                <Typography variant="body2">
                  Select "Add to Home Screen"
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  width: 24, 
                  height: 24, 
                  borderRadius: '50%', 
                  background: '#7ead49',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  3
                </Box>
                <Typography variant="body2">
                  Tap "Add" to install
                </Typography>
              </Box>
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => handleDismiss('ios')}
            variant="outlined"
            fullWidth
            sx={{ 
              borderRadius: '12px',
              borderColor: 'text.secondary',
              color: 'text.secondary'
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </StyledDialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            borderRadius: '12px',
            '& .MuiAlert-icon': { fontSize: '20px' }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PWAInstallPrompt; 